import { __awaiter, __decorate, __generator, __metadata } from "tslib";
import { inject, injectable } from 'inversify';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { apiServices } from '../../ioc/services';
import { SaveVideoProgressDto } from './dto/SaveVideoProgressDto';
import { JobStatus } from './JobStatus';
import { SaveVideoProgressStatus } from './types/VideoMakerApiPayload';
import { VideoMakerApi } from './VideoMakerApi';
var SECOND = 1000;
/**
 * Poll for:
 *  - video preview status creation with receiving progress updates.
 *  - creating high quality videos.
 *  - file save to user/site files.
 */
var VideoMakerJobQueue = /** @class */ (function () {
    function VideoMakerJobQueue() {
        this.jobs = new Map();
        this.POLL_INTERVAL = 5 * SECOND;
    }
    /**
     * Polls Video Maker Api until file is not saved.
     * Saved fileName can be found JobStatusDto.fileName
     *
     * @param job Job, that was rendered with saveTo by VideoMakerApi.renderVideo
     * @returns JobStatusDto with filled `renderVideo`
     */
    VideoMakerJobQueue.prototype.getFileName = function (job) {
        var _this = this;
        return new Observable(function (observer) {
            _this.poller(_this.POLL_INTERVAL, function (repeat) { return __awaiter(_this, void 0, void 0, function () {
                var status, fileName;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, this.videoMakerApi.getStatus(job).toPromise()];
                        case 1:
                            status = _a.sent();
                            if (status.status === JobStatus.ERROR) {
                                observer.error(new Error('Save video poller error in response'));
                                return [2 /*return*/];
                            }
                            fileName = status.fileName || undefined;
                            if (!fileName) {
                                repeat();
                                return [2 /*return*/];
                            }
                            observer.next(status);
                            observer.complete();
                            return [2 /*return*/];
                    }
                });
            }); });
        });
    };
    /**
     * Polls Video Maker Api until video render is not finished.
     * Path to high quality file can be found JobStatusDto.streamUrl
     *
     * @param job Job, that was rendered by VideoMakerApi.renderVideo
     * @returns JobStatusDto with filled `streamUrl`
     */
    VideoMakerJobQueue.prototype.waitForStreamUrl = function (job) {
        var _this = this;
        return new Observable(function (observer) {
            _this.poller(_this.POLL_INTERVAL, function (repeat) { return __awaiter(_this, void 0, void 0, function () {
                var status, streamUrl;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0: return [4 /*yield*/, this.videoMakerApi.getStatus(job).toPromise()];
                        case 1:
                            status = _a.sent();
                            if (status.status === JobStatus.ERROR) {
                                observer.error(new Error('Save video poller error in response'));
                                return [2 /*return*/];
                            }
                            streamUrl = status.streamUrl;
                            if (!streamUrl) {
                                repeat();
                                return [2 /*return*/];
                            }
                            observer.next(status);
                            observer.complete();
                            return [2 /*return*/];
                    }
                });
            }); });
        });
    };
    VideoMakerJobQueue.prototype.getProgressData = function (progressId) {
        var _this = this;
        return new Observable(function (observer) {
            _this.poller(_this.POLL_INTERVAL, function (repeat) { return __awaiter(_this, void 0, void 0, function () {
                var _a, status, progress, step;
                return __generator(this, function (_b) {
                    switch (_b.label) {
                        case 0: return [4 /*yield*/, this.videoMakerApi
                                .getSavingVideoProgress(progressId)
                                .pipe(catchError(function (error) {
                                observer.error(new Error(error));
                                return throwError(error);
                            }))
                                .toPromise()];
                        case 1:
                            _a = _b.sent(), status = _a.status, progress = _a.progress, step = _a.step;
                            if (status === SaveVideoProgressStatus.FAIL) {
                                observer.error(new Error('Save video process poller error in response'));
                                return [2 /*return*/];
                            }
                            if (!progress) {
                                repeat();
                                return [2 /*return*/];
                            }
                            observer.next(new SaveVideoProgressDto({ status: status, progress: progress, step: step }));
                            if (progress === 1) {
                                observer.complete();
                                return [2 /*return*/];
                            }
                            repeat();
                            return [2 /*return*/];
                    }
                });
            }); });
        });
    };
    /**
     * Poll Video Maker Api until preview is ready or failed
     * Also subject will emit JobStatusDto with progress and status of preview render
     * @param job Job that was created by VideoMakerApi.createVideo
     */
    VideoMakerJobQueue.prototype.addJob = function (job) {
        var ob = new Subject();
        this.jobs.set(job, ob);
        this.startPoll(job);
        return ob;
    };
    VideoMakerJobQueue.prototype.startPoll = function (job) {
        var _this = this;
        this.poller(this.POLL_INTERVAL, function (repeat) { return __awaiter(_this, void 0, void 0, function () {
            var status;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.videoMakerApi.getStatus(job).toPromise()];
                    case 1:
                        status = _a.sent();
                        switch (status.status) {
                            case JobStatus.DONE:
                            case JobStatus.ERROR:
                                this.updateJobStatus(job, status);
                                return [2 /*return*/];
                            default:
                                this.updateJobStatus(job, status);
                                repeat();
                                return [2 /*return*/];
                        }
                        return [2 /*return*/];
                }
            });
        }); });
    };
    VideoMakerJobQueue.prototype.updateJobStatus = function (job, newInfo) {
        var subject = this.jobs.get(job);
        if (!subject) {
            return;
        }
        switch (newInfo.status) {
            case JobStatus.DONE:
                subject.next(newInfo);
                subject.complete();
                break;
            case JobStatus.ERROR:
                subject.error(newInfo.reason || new Error('Unknown error during video creation'));
                break;
            default:
            case JobStatus.PROCESSING:
            case JobStatus.INIT:
                subject.next(newInfo);
        }
    };
    __decorate([
        inject(apiServices.videoMakerApi),
        __metadata("design:type", VideoMakerApi)
    ], VideoMakerJobQueue.prototype, "videoMakerApi", void 0);
    __decorate([
        inject(apiServices.poller),
        __metadata("design:type", Function)
    ], VideoMakerJobQueue.prototype, "poller", void 0);
    VideoMakerJobQueue = __decorate([
        injectable()
    ], VideoMakerJobQueue);
    return VideoMakerJobQueue;
}());
export { VideoMakerJobQueue };
//# sourceMappingURL=VideoMakerJobQueue.js.map